import { clienteDisplay } from '@/lib/types';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Bell, Plus, X, Loader2, AlertCircle, Trash2, Calendar, Check, ChevronRight,
} from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { LembretePrioridade, Solicitante } from '@/lib/types';

type Filtro = 'todos' | 'meus' | 'hoje' | 'atrasados' | 'concluidos';

function diasAteHoje(prazo?: string): number | null {
  if (!prazo) return null;
  const p = new Date(prazo); p.setHours(0,0,0,0);
  const h = new Date(); h.setHours(0,0,0,0);
  return Math.ceil((p.getTime() - h.getTime()) / 86400000);
}

const PRIO_COR: Record<LembretePrioridade, string> = {
  'Baixa': 'bg-green/15 text-green border-green/30',
  'Média': 'bg-orange/15 text-orange border-orange/30',
  'Alta':  'bg-red/15 text-red border-red/30',
};

export function Lembretes() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [filtro, setFiltro] = useState<Filtro>('meus');
  const [novoOpen, setNovoOpen] = useState(false);

  const lembretesQ = useQuery({ queryKey: ['lembretes'], queryFn: () => api.lembretes.list().then(r => r.data) });
  const solQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then(r => r.data) });
  const pessoasQ = useQuery({ queryKey: ['pessoas'], queryFn: () => api.pessoas.list().then(r => r.data) });

  const solById = useMemo(() => new Map((solQ.data ?? []).map(s => [s.id, s])), [solQ.data]);
  const pessoaById = useMemo(() => new Map((pessoasQ.data ?? []).map(p => [p.id, p])), [pessoasQ.data]);

  const filtrados = useMemo(() => {
    const lista = (lembretesQ.data ?? []);
    return lista.filter(l => {
      const dias = diasAteHoje(l.prazo);
      if (filtro === 'meus') return l.criadorId === user?.pessoaId && l.status === 'Pendente';
      if (filtro === 'hoje') return l.status === 'Pendente' && dias === 0;
      if (filtro === 'atrasados') return l.status === 'Pendente' && dias !== null && dias < 0;
      if (filtro === 'concluidos') return l.status === 'Concluído';
      return true;
    }).sort((a, b) => (a.prazo ?? '9999').localeCompare(b.prazo ?? '9999'));
  }, [lembretesQ.data, filtro, user?.pessoaId]);

  const cMeus = (lembretesQ.data ?? []).filter(l => l.criadorId === user?.pessoaId && l.status === 'Pendente').length;
  const cHoje = (lembretesQ.data ?? []).filter(l => l.status === 'Pendente' && diasAteHoje(l.prazo) === 0).length;
  const cAtrasados = (lembretesQ.data ?? []).filter(l => l.status === 'Pendente' && diasAteHoje(l.prazo) !== null && diasAteHoje(l.prazo)! < 0).length;

  const concluir = useMutation({
    mutationFn: (id: string) => api.lembretes.update(id, { status: 'Concluído' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lembretes'] }),
  });
  const apagar = useMutation({
    mutationFn: (id: string) => api.lembretes.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['lembretes'] }),
  });

  return (
    <div>
      <PageHeader
        title="Lembretes"
        action={<button type="button" onClick={() => setNovoOpen(true)} className="btn-primary !py-1.5 !px-3 text-xs"><Plus className="w-3.5 h-3.5" /> Novo lembrete</button>}
      />

      <div className="px-6 py-4 space-y-4">
        <div className="flex flex-wrap gap-1">
          {(['meus','todos','hoje','atrasados','concluidos'] as Filtro[]).map(f => {
            const labels: Record<Filtro,string> = {
              meus: `Meus (${cMeus})`,
              todos: `Todos (${lembretesQ.data?.length ?? 0})`,
              hoje: `Pra hoje (${cHoje})`,
              atrasados: `Atrasados (${cAtrasados})`,
              concluidos: 'Concluídos',
            };
            return (
              <button key={f} type="button" onClick={() => setFiltro(f)}
                className={clsx('px-3 py-1.5 text-[11px] font-semibold rounded border transition-colors',
                  filtro === f ? 'bg-blue text-white border-blue' : 'bg-panel text-text-2 border-border hover:border-border-light')}>
                {labels[f]}
              </button>
            );
          })}
        </div>

        {lembretesQ.isLoading && <div className="card text-sm text-muted">Carregando…</div>}

        {!lembretesQ.isLoading && filtrados.length === 0 && (
          <div className="card text-center py-12">
            <Bell className="w-10 h-10 text-muted mx-auto mb-2 opacity-40" />
            <p className="text-sm text-muted">Nenhum lembrete nessa categoria.</p>
          </div>
        )}

        <div className="space-y-2">
          {filtrados.map(l => {
            const dias = diasAteHoje(l.prazo);
            const atrasado = dias !== null && dias < 0 && l.status === 'Pendente';
            const hoje = dias === 0 && l.status === 'Pendente';
            const concluido = l.status === 'Concluído';
            const sol = l.solicitanteId ? solById.get(l.solicitanteId) : undefined;
            const criador = l.criadorId ? pessoaById.get(l.criadorId) : undefined;
            return (
              <div key={l.id} className={clsx(
                'card p-3 border-2 transition-colors',
                concluido && 'opacity-60 border-border',
                atrasado && !concluido && 'border-red/40 bg-red/5',
                hoje && !concluido && 'border-orange/40 bg-orange/5',
                !atrasado && !hoje && !concluido && 'border-blue/40',
              )}>
                <div className="flex items-start gap-2.5">
                  <button type="button" onClick={() => !concluido && concluir.mutate(l.id)} disabled={concluido}
                    className={clsx(
                      'w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 shrink-0 transition-colors',
                      concluido ? 'bg-green border-green text-white' : 'border-gray-400 hover:border-green hover:bg-green/10',
                    )}>
                    {concluido && <Check className="w-3 h-3" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <h3 className={clsx('text-[13px] font-bold', concluido && 'line-through')}>{l.titulo}</h3>
                      <div className="flex gap-1 shrink-0">
                        {l.prioridade && <span className={clsx('text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border', PRIO_COR[l.prioridade])}>{l.prioridade}</span>}
                      </div>
                    </div>
                    {l.descricao && <p className="text-[11.5px] text-text-2 mb-1.5">{l.descricao}</p>}
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10.5px] text-muted">
                      {l.prazo && (
                        <span className={clsx('inline-flex items-center gap-1',
                          atrasado ? 'text-red font-bold' : hoje ? 'text-orange font-bold' : '')}>
                          <Calendar className="w-2.5 h-2.5" />
                          {new Date(l.prazo).toLocaleDateString('pt-BR')}
                          {l.hora && ` · ${l.hora}`}
                          {dias !== null && (
                            dias === 0 ? ' · hoje' :
                            dias < 0 ? ` · ${Math.abs(dias)} dias atrasado` :
                            dias === 1 ? ' · amanhã' :
                            ` · em ${dias} dias`
                          )}
                        </span>
                      )}
                      {sol && <span className="inline-flex items-center gap-1"><ChevronRight className="w-2.5 h-2.5" />{clienteDisplay(sol.nome)}</span>}
                      {criador && <span>· por {criador.nome.split(' ')[0]}</span>}
                    </div>
                  </div>
                  <button type="button" onClick={() => apagar.mutate(l.id)} disabled={apagar.isPending}
                    className="text-muted hover:text-red text-xs p-1">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {novoOpen && (
        <NovoLembreteModal
          solicitantes={solQ.data ?? []}
          onClose={() => setNovoOpen(false)}
          onCreated={() => qc.invalidateQueries({ queryKey: ['lembretes'] })}
        />
      )}
    </div>
  );
}

function NovoLembreteModal({ solicitantes, onClose, onCreated }: {
  solicitantes: Solicitante[]; onClose: () => void; onCreated: () => void;
}) {
  const [titulo, setTitulo] = useState('');
  const [descricao, setDescricao] = useState('');
  const [prazo, setPrazo] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [hora, setHora] = useState('09:00');
  const [solicitanteId, setSolicitanteId] = useState('');
  const [prioridade, setPrioridade] = useState<LembretePrioridade>('Média');
  const [avisarDias, setAvisarDias] = useState(1);
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => api.lembretes.create({
      titulo: titulo.trim(),
      descricao: descricao.trim() || undefined,
      prazo,
      hora: hora || undefined,
      solicitanteId: solicitanteId || undefined,
      prioridade,
      avisarDiasAntes: avisarDias,
    }),
    onSuccess: () => { onCreated(); onClose(); },
    onError: (e) => setErr(e instanceof ApiError ? (e.body as { message?: string })?.message ?? e.message : String(e)),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" tabIndex={-1} ref={(el)=>{if(el&&el.dataset.esc!=='1'){el.dataset.esc='1';el.focus();}}} onKeyDown={(e)=>{if(e.key==='Escape')onClose();}}>
      <form onClick={e => e.stopPropagation()} onSubmit={e => { e.preventDefault(); if (!titulo.trim()) { setErr('Título obrigatório'); return; } setErr(null); create.mutate(); }}
        className="bg-panel border border-border rounded-xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-sm font-bold">Novo lembrete</h2>
          <button type="button" onClick={onClose} className="text-muted hover:text-text"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">Título *</label>
            <input autoFocus value={titulo} onChange={e => setTitulo(e.target.value)} className="input w-full text-sm" placeholder="Ex.: cobrar logo BVA do cliente" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">Descrição</label>
            <textarea value={descricao} onChange={e => setDescricao(e.target.value)} rows={2} className="input w-full text-sm resize-none" placeholder="Detalhe livre…" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">Data</label>
              <input type="date" value={prazo} onChange={e => setPrazo(e.target.value)} className="input w-full text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">Hora</label>
              <input type="time" value={hora} onChange={e => setHora(e.target.value)} className="input w-full text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">Prioridade</label>
              <select value={prioridade} onChange={e => setPrioridade(e.target.value as LembretePrioridade)} className="input w-full text-sm">
                <option>Baixa</option><option>Média</option><option>Alta</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">Cliente (opcional)</label>
            <select value={solicitanteId} onChange={e => setSolicitanteId(e.target.value)} className="input w-full text-sm">
              <option value="">— sem cliente vinculado —</option>
              {solicitantes.filter(s => s.status === 'Ativo').sort((a, b) => a.nome.localeCompare(b.nome)).map(s => (
                <option key={s.id} value={s.id}>{s.nome}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-muted block mb-1">Avisar quantos dias antes</label>
            <input type="number" min={0} max={30} value={avisarDias} onChange={e => setAvisarDias(parseInt(e.target.value) || 0)} className="input w-full text-sm" />
          </div>
          {err && <div className="bg-red/10 border border-red/30 text-red text-xs rounded-md p-2 flex gap-2"><AlertCircle className="w-3.5 h-3.5 mt-0.5" /><span>{err}</span></div>}
        </div>
        <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-secondary !py-1.5 !px-3 text-xs">Cancelar</button>
          <button type="submit" disabled={create.isPending} className="btn-primary !py-1.5 !px-3 text-xs disabled:opacity-50">
            {create.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Criar
          </button>
        </div>
      </form>
    </div>
  );
}
